import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { getPatientList } from 'apis/patient';
import SearchFilter from 'components/common/forms/SearchFilter';
import Footer from 'components/common/layout/Footer';
import Layout from 'components/common/layout/Layout';
import TableForm from 'components/common/lists/TableForm';
import Paging from 'components/common/nav/Paging';
import { ROUTE_PATH } from 'constants/routePath';
import { DEFAULT_PAGE_SIZE } from 'constants/page';
import { SortDirection } from 'types/apis/common';
import { PatientInfoSortBy, TPatientListParams } from 'types/apis/patient';
import {
  GetPatientListFormFields,
  PatientInfoSex,
  PatientInfoSortByFormLabel,
} from 'types/form/patient';
import { TTableColumn } from 'types/components/table';

const tableColumns: TTableColumn[] = [
  {
    name: 'no',
    label: '번호',
    width: '80px',
  },
  {
    name: PatientInfoSortBy.Name,
    label: PatientInfoSortByFormLabel.Name,
    width: '126px',
    onClick: () => {
      return;
    },
  },
  {
    name: PatientInfoSortBy.BirthAndAge,
    label: PatientInfoSortByFormLabel.BirthAndAge,
    width: '188px',
    onClick: () => {
      return;
    },
  },
  {
    name: PatientInfoSortBy.Sex,
    label: PatientInfoSortByFormLabel.Sex,
    width: '104px',
    onClick: () => {
      return;
    },
  },
  {
    name: 'phoneNum',
    label: '연락처',
    width: '138px',
  },
  {
    name: PatientInfoSortBy.Occupation,
    label: PatientInfoSortByFormLabel.Occupation,
    width: '106px',
    onClick: () => {
      return;
    },
  },
  {
    name: PatientInfoSortBy.Relation,
    label: PatientInfoSortByFormLabel.Relation,
    width: '136px',
    onClick: () => {
      return;
    },
  },
  {
    name: PatientInfoSortBy.CreatedAt,
    label: PatientInfoSortByFormLabel.CreatedAt,
    width: '142px',
    onClick: () => {
      return;
    },
  },
];

const PatientManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  const [page, setPage] = useState(Number(query.get('page')) || 1);
  const [params, setParams] = useState<TPatientListParams>({
    page,
    size: DEFAULT_PAGE_SIZE,
    searchWord: query.get('searchWord') || undefined,
    startDate: query.get('startDate') || undefined,
    endDate: query.get('endDate') || undefined,
    sex: query.getAll('sex') || undefined,
    sortBy: (query.get('sortBy') as PatientInfoSortBy) || undefined,
    isAsc:
      query.get('sort') === SortDirection.Asc
        ? true
        : query.get('sort') === SortDirection.Desc
        ? false
        : undefined,
  });
  const [sortBy, setSortBy] = useState(query.get('sortBy') || '');
  const [sortDirection, setSortDirection] = useState(query.get('sort') || '');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (sortBy !== query.get('sortBy')) setSortBy('');
    if (sortDirection !== query.get('sort')) setSortDirection('');
    setParams({
      ...params,
      page,
      searchWord: query.get('searchWord') || undefined,
      startDate: query.get('startDate') || undefined,
      endDate: query.get('endDate') || undefined,
      sex: query.getAll('sex') || undefined,
    });
  }, [location.search, page]);

  const { data: getPatientListData, isFetching } = useQuery(
    ['getPatientList', params],
    () => getPatientList(params)
  );

  const patientListData = getPatientListData?.data.patientList || [];
  const patientListPaging = getPatientListData?.data.paging;

  useEffect(() => {
    setIsLoading(isFetching);
  }, [isFetching]);

  const onClickSort = (label: string) => {
    const sortAsc =
      sortDirection === ''
        ? SortDirection.Asc
        : sortDirection === SortDirection.Asc
        ? SortDirection.Desc
        : '';
    const sortBy = {
      [PatientInfoSortByFormLabel.Name]: PatientInfoSortBy.Name,
      [PatientInfoSortByFormLabel.BirthAndAge]: PatientInfoSortBy.BirthAndAge,
      [PatientInfoSortByFormLabel.Sex]: PatientInfoSortBy.Sex,
      [PatientInfoSortByFormLabel.Occupation]: PatientInfoSortBy.Occupation,
      [PatientInfoSortByFormLabel.Relation]: PatientInfoSortBy.Relation,
      [PatientInfoSortByFormLabel.CreatedAt]: PatientInfoSortBy.CreatedAt,
    }[label as PatientInfoSortByFormLabel];

    setSortDirection(sortAsc);
    setSortBy(sortBy);

    if (page !== 1) {
      setPage(1);
    }
    if (!sortAsc) {
      query.delete('sort');
      query.delete('sortBy');
    } else {
      query.set('sort', sortAsc);
      query.set('sortBy', sortBy);
    }
    navigate({
      search: query.toString(),
    });

    setParams({
      ...params,
      page: 1,
      sortBy: (query.get('sortBy') as PatientInfoSortBy) || undefined,
      isAsc:
        sortAsc === SortDirection.Asc
          ? true
          : sortAsc === SortDirection.Desc
          ? false
          : undefined,
    });
  };

  return (
    <div className="min_h100vh flex flex_column">
      <div className="grow_1">
        <Layout bgColor={true} hideFooter={true}>
          <SearchFilter
            isCollective={true}
            input={{
              placeholder: '대상자 이름 또는 연락처를 입력해 주세요.',
              width: '488px',
            }}
            dateRange={{
              label: '등록일',
              width: '232px',
            }}
            checkboxLabel="성별"
            checkbox={[
              {
                id: GetPatientListFormFields.Sex,
                label: [PatientInfoSex.Male, PatientInfoSex.Female],
              },
            ]}
          />
        </Layout>

        <div className="table_container">
          <h1 className="main_title mt40 mb16">대상자 목록</h1>
          <TableForm
            data={patientListData}
            columns={tableColumns}
            paging={patientListPaging}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onClickSort={onClickSort}
            onClickRowById={(id) =>
              navigate(`${ROUTE_PATH.student.PATIENT}/${id}`)
            }
            idField="patientId"
            emptyMessage="등록된 대상자가 없습니다."
            isLoading={isLoading}
          />
        </div>

        {patientListPaging && patientListPaging.totalElements > 0 && (
          <div className="mt32">
            <Paging
              activePage={page}
              totalItemsCount={patientListPaging.totalElements}
              handlePageChange={setPage}
            />
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default PatientManagement;
